
import { ICoordinates, IVacancy, IVacancyMapPoint } from '@tokyojob/core'
import Vue, { PropType } from 'vue'
import CMapFindMyLocationComponent from './c-map-find-my-location.component.vue'
import CMapSearchBarComponent from './c-map-search-bar.component.vue'
import CVacancyMapComponent from './c-vacancy-map.component.vue'
import { CRibbon } from '@tokyojob/frontend-components'
import CMapError from './c-map-error.component.vue'
import { IFormInputData } from '@tokyojob/frontend-core'
import { vacancyCardSmallMapUpdatedEvent } from '../c-vacancy-card-small-map/constants/events'

const vacancyRibbonMessage = ' jobs available in the area'

interface IData {
  /**
   *
   */
  vacancyMap: InstanceType<typeof CVacancyMapComponent> | undefined
  /**
   * The user's current location in the map.
   */
  currentLocationCoordinates?: ICoordinates

  /**
   * Error message generated while trying to fetch the GPS coordinates.
   */
  gpsError?: string
}

export default Vue.extend({
  components: {
    CVacancyMapComponent,
    CMapSearchBarComponent,
    CMapFindMyLocationComponent,
    CRibbon,
    CMapError
  },
  props: {
    /**
     * Initial central location of the map.
     */
    coordinates: {
      required: true,
      type: Object as PropType<ICoordinates>
    },
    /**
     * Name of the html element in which the map should be displayed. Also used
     * as part of the name of the autocomplete field.
     */
    mapId: {
      required: true,
      type: String
    },
    /**
     * Points in the map that contain vacancies.
     */
    points: {
      required: true,
      type: Array as PropType<IVacancyMapPoint[]>
    },
    /**
     * Helps determine some of the map's settings, like zoom limits.
     */
    isMobile: {
      required: false,
      type: Boolean,
      default: false
    },
    /**
     * The list of visa types available.
     */
    visaTypes: {
      type: Array as PropType<IFormInputData[]>,
      required: true
    },
    /**
     * Array of all currently selected Visas
     */
    selectedVisaTypes: {
      type: Array as PropType<IFormInputData[]>,
      required: true
    }
  },
  data(): IData {
    return {
      gpsError: '',
      currentLocationCoordinates: undefined,
      vacancyMap: undefined
    }
  },
  methods: {
    /**
     * Updates the center coordinate
     */
    updateCenterCoordinates(coordinates: ICoordinates): void {
      this.$emit('updateCenterCoordinates', coordinates)
    },
    /**
     * Updates the user's current location.
     */
    updateCurrentLocationCoordinates(coordinates: ICoordinates): void {
      this.currentLocationCoordinates = coordinates
      this.updateCenterCoordinates(coordinates)
    },
    /**
     * Triggered when a marker (NOT a cluster) is clicked in the map
     */
    onMarkerClicked(marker: google.maps.Marker): void {
      this.$emit('markerClicked', marker)
    },
    /**
     * Triggers when an info window with more than 1 vacancy has its
     * displayed vacancy updated.
     */
    onDisplayedVacancyChange(vacancy: IVacancy): void {
      this.$emit(vacancyCardSmallMapUpdatedEvent, vacancy)
    },
    /**
     * Selects and centers a marker in the map.
     */
    highlightMarker(item: IVacancy, point: IVacancyMapPoint): void {
      if (!this.vacancyMap) {
        const mapFrame = this.$refs['vacancyMap'] as InstanceType<typeof CVacancyMapComponent>
        this.vacancyMap = mapFrame
      }
      this.vacancyMap.highlightMarker(item, point)
    },
    /**
     * Sets the gps error message.
     */
    onPositionError(errorMsg: string): void {
      this.gpsError = errorMsg
    },
    /**
     * Delete the error message, closing the error message div
     */
    resetError() {
      this.gpsError = ''
    },
    /**
     * Pass event informing parent an info window has been opened
     */
    onInfoWindowOpen(marker: google.maps.Marker) {
      this.$emit('hideFilterBar', marker)
    },
    /**
     * Pass event informing parent an info window has been closed
     */
    onInfoWindowClose(marker: google.maps.Marker) {
      this.$emit('showFilterBar', marker)
    }
  },
  computed: {
    vacancyCounter: {
      get() {
        let counter = 0
        for (const point of this.points) {
          counter += point.vacancies.length
        }
        return [counter + vacancyRibbonMessage]
      }
    }
  }
})
